import type { Uuid } from "spacetimedb";
import { useState } from "react";
import { InlineWidget, useCalendlyEventListener } from "react-calendly";

import { useUser } from "@/web/auth/useUser";
import { Button } from "@/web/components/primitives/button";
import {
  Drawer,
  DrawerBody,
  DrawerCloseIcon,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from "@/web/components/primitives/drawer";
import { env } from "@/web/lib/env";
import { ErrorHandler } from "@/web/lib/errors";
import { useProcedureMutation } from "@/web/lib/tanstack";
import { procedures } from "@tycho/web-bindings";
import { ActionCard } from "@/web/routes/_protected/smb/-dashboard/-ActionCard";
import { CardRulesEngine } from "@/web/routes/_protected/smb/-dashboard/-CardRulesEngine";
import { createScheduleCallCompleted } from "@/web/routes/_protected/smb/-dashboard/-completedActions/ScheduleCallCompleted";
import { useSuspendedSmbSuperView } from "@/web/routes/_protected/smb/-dashboard/superView";
import type {
  SmbActionCoreProps,
  SmbActionDefinition,
} from "@/web/routes/_protected/smb/-dashboard/-types";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/web/components/primitives/tooltip";

// Overridable so the opt-in real-integrations e2e spec can point at a throwaway
// test event type (see VITE_CALENDLY_URL in lib/env.ts); prod/staging leave it
// unset and get the real booking link.
const CALENDLY_URL = env.VITE_CALENDLY_URL ?? "https://calendly.com/with-kevin/25-min";

type ScheduleCallActionProps = {
  underwritableEntity: Uuid;
  /**
   * Whether the brand is currently allowed to open the scheduling drawer.
   * The rule's `build` derives this from the card-engine data; the action
   * itself does not need to know about the engine shape.
   */
  canScheduleCall: boolean;
};

function ScheduleCallAction({
  underwritableEntity,
  canScheduleCall,
  onSkip,
}: ScheduleCallActionProps & SmbActionCoreProps) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [callScheduled, setCallScheduled] = useState(false);
  const user = useUser();
  const superView = useSuspendedSmbSuperView(underwritableEntity);
  const { mutateAsync: createCalendlyBooking } = useProcedureMutation(
    procedures.createCalendlyBooking,
  );
  const _ = useCalendlyEventListener({
    onEventScheduled(e) {
      createCalendlyBooking({
        underwritableEntity,
        eventUri: e.data.payload.event.uri,
      })
        .then(() => setCallScheduled(true))
        .catch(ErrorHandler.default);
    },
  });

  return (
    <>
      <ActionCard category="generic">
        <ActionCard.Header>
          <ActionCard.Badge>Checkpoint</ActionCard.Badge>
        </ActionCard.Header>
        <ActionCard.Main>
          <ActionCard.Title>Make an offer</ActionCard.Title>
          <ActionCard.Description>
            {canScheduleCall ? (
              <>
                You're ready to take a major step towards ownership. Get in touch with us to review
                a letter of intent (LOI) and start negotiating.
              </>
            ) : (
              <>
                You're currently working toward a letter of intent (LOI) that we can review
                together. Keep answering questions to move forward.
              </>
            )}
          </ActionCard.Description>
          {/* TODO: graphz */}
        </ActionCard.Main>
        <ActionCard.Footer>
          <ActionCard.SkipButton color="white" onSkip={onSkip}>
            Skip
          </ActionCard.SkipButton>
          {!canScheduleCall ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="inline-flex w-full">
                  <ActionCard.PrimaryButton onClick={() => setDrawerOpen(true)} disabled>
                    Schedule a Call
                  </ActionCard.PrimaryButton>
                </span>
              </TooltipTrigger>
              <TooltipContent color="white" side="top">
                You'll be able to do this after you answer a few more questions. Check out the other
                cards to keep collaborating with us.
              </TooltipContent>
            </Tooltip>
          ) : (
            <ActionCard.PrimaryButton onClick={() => setDrawerOpen(true)}>
              Schedule a Call
            </ActionCard.PrimaryButton>
          )}
        </ActionCard.Footer>
      </ActionCard>
      <Drawer open={drawerOpen} onOpenChange={setDrawerOpen}>
        <DrawerContent>
          <DrawerHeader>
            <DrawerTitle>Schedule a call with us</DrawerTitle>
            <DrawerCloseIcon />
          </DrawerHeader>
          <DrawerBody>
            <InlineWidget
              url={CALENDLY_URL}
              prefill={{
                email: user?.email ?? "",
                name: superView.brand.friendlyName,
              }}
              // TODO: pageSettings for colors? can't get it working
              styles={{
                height: "100%",
                width: "100%",
              }}
            />
          </DrawerBody>

          <DrawerFooter>
            <Button onClick={() => setDrawerOpen(false)} disabled={!callScheduled}>
              Done
            </Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    </>
  );
}

function createScheduleCallAction(
  props: ScheduleCallActionProps & { id?: string; canSkip?: boolean },
): SmbActionDefinition {
  const { id = "schedule-call", canSkip = true, ...rest } = props;
  return {
    id,
    category: "generic",
    canSkip,
    render: (core) => <ScheduleCallAction {...rest} {...core} />,
  };
}

// ── Engine rules ─────────────────────────────────────────────────────────────

export const scheduleCallRule = CardRulesEngine.singletonRule({
  id: "schedule-call",
  // PM ask: keep the card visible at low priority whenever the brand still
  // needs to schedule (no prior calls). Gating on `hasNoScheduledCalls` keeps
  // this rule mutually exclusive with the inverse `scheduleCallCompletedRule`,
  // so the active deck clears once a call is booked and the action moves to
  // the completed view as usual. The card itself disables the primary button
  // when `initialQuestionsComplete` is unset (see `canScheduleCall` below).
  //
  // Hidden for invited brands.
  priority: 10,
  when: CardRulesEngine.and(
    CardRulesEngine.hasNoScheduledCalls,
    CardRulesEngine.not(CardRulesEngine.businessStoryIs("Invited")),
  ),
  build: (d) => {
    const latestUnderwritingResult = d.underwritingResults.toSorted(
      (a, b) => new Date(b.runDate ?? 0).getTime() - new Date(a.runDate ?? 0).getTime(),
    )[0];
    const canScheduleCall = latestUnderwritingResult?.initialQuestionsComplete === true;
    return createScheduleCallAction({
      underwritableEntity: d.underwritableEntityId,
      canScheduleCall,
    });
  },
});

export const scheduleCallCompletedRule = CardRulesEngine.singletonRule({
  id: "schedule-call:done",
  priority: 100, // TODO: adjust
  when: CardRulesEngine.and(
    CardRulesEngine.not(CardRulesEngine.hasNoScheduledCalls),
    CardRulesEngine.not(CardRulesEngine.businessStoryIs("Invited")),
  ),
  build: (d) => {
    const scheduledCall = d.scheduledCalls[0];
    // shouldn't happen?
    if (!scheduledCall) throw new Error("No scheduled call found");
    return createScheduleCallCompleted({ scheduledCall });
  },
});
